import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import { Box, CircularProgress, LinearProgress, Link, Typography, useTheme } from '@mui/material';
import type { FC, ReactNode } from 'react';

import { SquaredBadge } from '../SquaredBadge/SquaredBadge';
import type { AsyncJobStatusPresentation } from './AsyncJobStatusPresentation';

type Props = {
  presentation: AsyncJobStatusPresentation;
  failureActionLabel?: string;
  onViewFailures: () => void;
  failurePreview?: ReactNode;
};

export const AsyncJobStatusContent: FC<Props> = ({
  presentation,
  failureActionLabel,
  onViewFailures,
  failurePreview,
}) => {
  const theme = useTheme();
  const failureDetails = presentation.hasFailedItems
    ? (failurePreview ??
      (failureActionLabel !== undefined ? (
        <Link
          component='button'
          underline='none'
          onClick={onViewFailures}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            color: theme.palette.SlabGray[800],
            '&:hover': {
              color: theme.palette.SlabGray[600],
            },
          }}
        >
          <Typography variant='body2' fontWeight={500}>
            {failureActionLabel}
          </Typography>
          <SquaredBadge badgeContent={presentation.failedCount} />
        </Link>
      ) : null))
    : null;

  if (presentation.isInProgress) {
    const progressColor =
      presentation.tone === 'warning'
        ? theme.palette.SlabYellow[600]
        : theme.palette.SlabIndigo[500];

    return (
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          {presentation.tone === 'warning' ? (
            <ErrorOutline sx={{ fontSize: 22, color: theme.palette.SlabYellow[800] }} />
          ) : (
            <CircularProgress
              size={20}
              thickness={5}
              sx={{ color: theme.palette.SlabIndigo[500] }}
            />
          )}
          <Typography variant='body2' color='text.secondary'>
            {presentation.primaryLabel}
          </Typography>
        </Box>
        <LinearProgress
          variant='determinate'
          value={presentation.progressPercent ?? 0}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: theme.palette.SlabGray[100],
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              backgroundColor: progressColor,
            },
          }}
        />

        {presentation.secondaryLabel !== undefined && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant='body2' color='text.secondary'>
              {presentation.secondaryLabel}
            </Typography>
            {failureDetails}
          </Box>
        )}
      </Box>
    );
  }

  if (presentation.state === 'succeeded') {
    return (
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleOutline sx={{ fontSize: 22, color: theme.palette.SlabGreen[500] }} />
          <Typography variant='body2'>{presentation.primaryLabel}</Typography>
        </Box>
      </Box>
    );
  }

  if (presentation.state === 'partial') {
    return (
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <ErrorOutline sx={{ fontSize: 22, color: theme.palette.SlabYellow[800] }} />
          <Box>
            <Typography variant='body2' fontWeight={600}>
              {presentation.primaryLabel}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {presentation.secondaryLabel}
            </Typography>
          </Box>
        </Box>

        {failureDetails}
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ErrorOutline sx={{ fontSize: 22, color: theme.palette.SlabRed[500] }} />
        <Box>
          <Typography variant='body2' fontWeight={600}>
            {presentation.primaryLabel}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {presentation.secondaryLabel}
          </Typography>
        </Box>
      </Box>
      {failureDetails !== null && <Box sx={{ mt: 1.5 }}>{failureDetails}</Box>}
    </Box>
  );
};
